import { prettyMilliseconds } from "@-/com.npmjs.pretty-ms";
import {
  persistentKeyboardKeepFocused,
  persistentKeyboardPreserveFocus,
  usePersistentKeyboardVisible,
} from "@-/dev.improve.haniverse.components.collapsible-persistent-keyboard";
import { useToolbarGridMetrics } from "@-/dev.improve.haniverse.components.ui.toolbar.button.view";
import { useReviewCardFsrsResolutionQueryOptions } from "@-/dev.improve.haniverse.lib.cards.study-card-data";
import {
  previewIntervals,
  REVIEW_RATING,
  type ReviewRating,
} from "@-/dev.improve.haniverse.lib.cards.user-card.fsrs";
import type { IconName } from "@-/dev.improve.haniverse.components.ui.icon";
import type { DrillType } from "@-/dev.improve.haniverse.lib.drill.entities";
import { LearnLayoutContext } from "@-/dev.improve.haniverse.routes.nav.app.learn._context";
import { useQuery } from "@-/com.npmjs.tanstack__react-query";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { View } from "@-/com.npmjs.react-native";
import { useMemo } from "react";
import { LearnFlashcardActionButton } from "#learn-card-action-button";
const REVIEW_RATING_OPTION_LIST = [
  { icon: "HanReviewAgainFace", label: "Again", rating: REVIEW_RATING.Again },
  { icon: "HanReviewHardFace", label: "Hard", rating: REVIEW_RATING.Hard },
  { icon: "HanReviewGoodFace", label: "Good", rating: REVIEW_RATING.Good },
  { icon: "HanReviewEasyFace", label: "Easy", rating: REVIEW_RATING.Easy },
] as const satisfies readonly { icon: IconName; label: string; rating: ReviewRating }[];

/** Ratings remain mounted while the selected action saves or loads its successor. */
export function LearnFlashcardRatingButtons({
  drillType,
  grapheme,
}: {
  drillType: DrillType;
  grapheme: string;
}) {
  const [presetRating] = useContextSelectors(
    LearnLayoutContext,
    (c) => c.answerInputState.presetRating,
  );
  // With the keyboard up the row shares its band with the root-level dismiss chevron, so the
  // ratings squish right by exactly that button's footprint and switch to condensed pills.
  const isKeyboardVisible = usePersistentKeyboardVisible();
  const { diameter, isLandscape } = useToolbarGridMetrics();
  const { data: fsrsResolution } = useQuery(
    useReviewCardFsrsResolutionQueryOptions(grapheme, drillType),
  );
  const intervalPreview = useMemo(
    () => (fsrsResolution == null ? null : previewIntervals(fsrsResolution.fsrsCard, new Date())),
    [fsrsResolution],
  );
  if (fsrsResolution === null) {
    return (
      <View className="size-full">
        <LearnFlashcardActionButton
          action={{ kind: "continue" }}
          handlesAllActions
          label="Continue"
          swipeDirection="both"
          testID="review-continue"
        />
      </View>
    );
  }
  return (
    <View
      className={
        isLandscape
          ? "size-full items-stretch gap-2.25"
          : "size-full flex-row items-stretch gap-2.25"
      }
      // Reserve the dismiss slot inside the row's width so Easy keeps the footer's 9px inset.
      style={isKeyboardVisible && !isLandscape ? { paddingLeft: diameter + 9 } : undefined}
    >
      {REVIEW_RATING_OPTION_LIST.map(({ icon, label, rating }) => (
        <View className={isLandscape ? "min-h-0 w-full flex-1" : "min-w-0 flex-1"} key={rating}>
          <LearnFlashcardActionButton
            action={{ kind: "rate", rating }}
            compact
            detail={
              intervalPreview === null
                ? "…"
                : prettyMilliseconds(Math.max(60_000, intervalPreview[rating].dueMs), {
                    compact: true,
                  })
            }
            detailAccessibilityLabel={
              intervalPreview === null
                ? undefined
                : prettyMilliseconds(Math.max(60_000, intervalPreview[rating].dueMs), {
                    compact: true,
                    verbose: true,
                  })
            }
            disabled={intervalPreview === null}
            icon={icon}
            label={label}
            onPressIn={() => {
              persistentKeyboardKeepFocused();
              void persistentKeyboardPreserveFocus();
            }}
            pill={isKeyboardVisible}
            selected={rating === presetRating}
            swipeDirection={
              rating === REVIEW_RATING.Again ? -1 : rating === REVIEW_RATING.Easy ? 1 : undefined
            }
            testID={"review-rating-" + label.toLowerCase()}
          />
        </View>
      ))}
    </View>
  );
}
